import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RULES, RoutineInput, jstDate, type Driver, type Routine, type RoutineRepeat, type Template, lockKeys } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { useDirectory, useNames } from '../lib/directory';
import { keepPlacesForDriver, putNote, putRoutineName, readNote, routineNoteId } from '../lib/fs';
import { useMe } from '../lib/me';
import { useLive } from '../lib/realtime';
import { Dialog, useToast } from '../components/ui';
import { LockBanner, useEditLock } from '../lib/locks';

const WEEK = ['日', '月', '火', '水', '木', '金', '土'];
export const repeatText = (r: RoutineRepeat) =>
  r.type === 'daily' ? '毎日' : r.type === 'weekly' ? `毎週 ${r.days.slice().sort().map((d) => WEEK[d]).join('・')}` : `毎月 ${r.days.slice().sort((a, b) => a - b).map((d) => (d === 32 ? '月末' : `${d} 日`)).join('・')}`;

/**
 * 定期便の登録（仕様書「定期便の登録」）：ひな形＋くり返し＋送る時刻＋担当。
 * 保存すると 3 ヶ月先まで予定を作り、送る時刻に担当のアプリへ自動で届く。担当には、定期便の行き先を見せ続ける印を付ける
 */
export function RoutinesPage() {
  const toast = useToast();
  const me = useMe();
  const names = useNames();
  const [rows, setRows] = useState<Routine[] | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [editing, setEditing] = useState<Routine | 'new' | null>(null);
  const [stopping, setStopping] = useState<Routine | null>(null);
  const load = () => api.get<{ routines: Routine[] }>('/v1/office/routines').then((r) => setRows(r.routines)).catch(() => setRows([]));
  useEffect(() => {
    void load();
    api.get<{ templates: Template[] }>('/v1/office/templates').then((r) => setTemplates(r.templates)).catch(() => {});
    api.get<{ drivers: Driver[] }>('/v1/office/drivers').then((r) => setDrivers(r.drivers)).catch(() => {});
  }, []);
  useLive(['deliveries'], load);
  const placesOf = (templateId: string) => templates.find((t) => t.id === templateId)?.stops.map((s) => s.placeId) ?? [];
  /** 担当から外れた人の印を外す（ほかの動いている定期便で使う場所は残す） */
  const releaseMarks = async (driverId: string, placeIds: string[], all: Routine[]) => {
    if (!me) return;
    const still = new Set(all.filter((r) => r.status === 'active' && r.driverId === driverId).flatMap((r) => placesOf(r.templateId)));
    await keepPlacesForDriver(me.officeId, driverId, placeIds.filter((p) => !still.has(p)), false).catch(() => {});
  };
  const stop = async (r: Routine) => {
    try {
      await api.post(`/v1/office/routines/${r.id}/stop`, {});
      const next = (rows ?? []).map((x) => (x.id === r.id ? { ...x, status: 'stopped' as const } : x));
      if (r.driverId) await releaseMarks(r.driverId, placesOf(r.templateId), next);
      toast('定期便を止めました。先の予定は消え、済んだ配送の記録は残ります');
      setStopping(null); void load();
    } catch (e) { toast(e instanceof HttpError ? e.message : '止められませんでした'); }
  };
  return (
    <div className="page" style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Link to="/schedule" className="btn" style={{ textDecoration: "none", color: "inherit" }}>← 月間スケジュール</Link>
        <button className="btn pri" onClick={() => setEditing('new')} disabled={!templates.length} title={templates.length ? '' : '先に配送指示の入力でひな形を作ってください'}>＋ 定期便を登録</button>
      </div>
      {!templates.length && <p className="muted" style={{ margin: 0 }}>定期便はひな形（行き先の並び）から作ります。先に「配送指示」でひな形に保存してください。</p>}
      <section className="panel">
        <table>
          <thead><tr><th>名前</th><th>行き先（ひな形）</th><th>くり返し</th><th>送る時刻</th><th>担当</th><th>期間</th><th>状態</th><th></th></tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={8} className="muted">読み込み中…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={8} className="muted">まだ定期便がありません</td></tr>
              : rows.map((r) => (
                <tr key={r.id} style={{ opacity: r.status === 'stopped' ? 0.5 : 1 }}>
                  <td><b>{names.routine(r.id)}</b></td><td>{names.template(r.templateId)}</td><td>{repeatText(r.repeat)}{r.runOnHolidays ? '' : '（祝日は休み）'}</td>
                  <td className="num">{r.sendTime}</td><td>{r.driverId ? names.driver(r.driverId) : <b className="err">未定</b>}</td>
                  <td className="num">{r.startDate}〜{r.endDate ?? ''}</td><td>{r.status === 'active' ? '動いている' : '止めた'}</td>
                  <td>{r.status === 'active' && <span style={{ display: 'flex', gap: 4 }}><button className="btn sm" onClick={() => setEditing(r)}>直す</button><button className="btn sm danger" onClick={() => setStopping(r)}>止める</button></span>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>
      {editing && <RoutineDialog routine={editing === 'new' ? null : editing} templates={templates} drivers={drivers} onClose={() => setEditing(null)}
        onSaved={async (saved, before) => {
          if (me && saved.driverId) await keepPlacesForDriver(me.officeId, saved.driverId, placesOf(saved.templateId), true).catch(() => toast('行き先の情報を担当に渡せませんでした。もう一度保存してください'));
          const all = (rows ?? []).filter((x) => x.id !== saved.id).concat(saved);
          if (before?.driverId && (before.driverId !== saved.driverId || before.templateId !== saved.templateId)) await releaseMarks(before.driverId, placesOf(before.templateId), all);
          setEditing(null); void load();
        }} />}
      {stopping && (
        <Dialog title={`「${names.routine(stopping.id)}」を止めますか？`} onClose={() => setStopping(null)}>
          <p style={{ margin: 0 }}>今日から先の、まだ送っていない予定を消します。済んだ配送の記録は残ります。</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={() => setStopping(null)}>やめる</button><button className="btn danger" onClick={() => void stop(stopping)}>止める</button></div>
        </Dialog>
      )}
    </div>
  );
}

function RoutineDialog({ routine, templates, drivers, onClose, onSaved }: {
  routine: Routine | null; templates: Template[]; drivers: Driver[]; onClose: () => void; onSaved: (r: Routine, before: Routine | null) => Promise<void>;
}) {
  const me = useMe();
  const toast = useToast();
  const names = useNames();
  const dir = useDirectory();
  const today = jstDate(new Date().toISOString());
  const [name, setName] = useState(routine ? names.routine(routine.id).replace('（名前なし）', '') : '');
  const [f, setF] = useState({
    templateId: routine?.templateId ?? templates[0]?.id ?? '', startDate: routine?.startDate ?? today, endDate: routine?.endDate ?? '', sendTime: routine?.sendTime ?? '08:00',
    driverId: routine?.driverId ?? '', runOnHolidays: routine?.runOnHolidays ?? false,
  });
  const [type, setType] = useState<RoutineRepeat['type']>(routine?.repeat.type ?? 'weekly');
  const [week, setWeek] = useState<number[]>(routine?.repeat.type === 'weekly' ? routine.repeat.days : [1, 2, 3, 4, 5]);
  const [monthDays, setMonthDays] = useState(routine?.repeat.type === 'monthly' ? routine.repeat.days.filter((d) => d !== 32).join(',') : '1');
  const [monthEnd, setMonthEnd] = useState(routine?.repeat.type === 'monthly' ? routine.repeat.days.includes(32) : false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const lock = useEditLock(routine ? lockKeys.routine(routine.id) : null);
  // 事務所からのメモ（毎回の依頼に出す。Firestore の notes/rtn_{定期便}、担当宛て）
  const [memo, setMemo] = useState('');
  const [hadMemo, setHadMemo] = useState(false);
  useEffect(() => { if (routine) void readNote(routineNoteId(routine.id)).then((t) => { setMemo(t); setHadMemo(!!t); }).catch(() => {}); }, [routine?.id]);
  const save = async () => {
    setErr('');
    const n = name.trim();
    if (!n || n.length > 40) { setErr('名前を 40 文字までで入れてください'); return; }
    const days = monthDays.split(/[,、\s]+/).filter(Boolean).map(Number);
    if (type === 'monthly' && days.some((d) => !Number.isInteger(d) || d < 1 || d > 31)) { setErr('日付は 1〜31 の数字を「,」で区切って入れてください'); return; }
    const repeat: RoutineRepeat = type === 'daily' ? { type } : type === 'weekly' ? { type, days: week } : { type, days: [...days, ...(monthEnd ? [32] : [])] };
    const r = RoutineInput.safeParse({ ...f, repeat, endDate: f.endDate || null, driverId: f.driverId || null });
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    if (!me) return;
    setBusy(true);
    try {
      let id = routine?.id, version = routine?.version ?? 1;
      if (routine) version = (await api.patch<{ version: number }>(`/v1/office/routines/${routine.id}`, r.data, { ifMatch: routine.version })).version;
      else id = (await api.post<{ id: string }>('/v1/office/routines', r.data)).id;
      await putRoutineName(id!, { officeId: me.officeId, name: n }).catch(() => toast('名前を保存できませんでした。もう一度保存してください'));
      if (memo.trim() || hadMemo) await putNote(routineNoteId(id!), { officeId: me.officeId, kind: 'batch', text: memo.trim(), ...(r.data.driverId ? { driverId: r.data.driverId } : {}) }).catch(() => toast('メモを保存できませんでした。もう一度保存してください'));
      toast(routine ? '定期便を直しました。先の予定を作り直しました' : '定期便を登録しました。3 ヶ月先まで予定を作りました');
      await onSaved({ ...r.data, id: id!, version, status: 'active', endDate: r.data.endDate, driverId: r.data.driverId }, routine);
    } catch (e) { setErr(e instanceof HttpError ? e.message : '保存できませんでした'); setBusy(false); }
  };
  return (
    <Dialog title={routine ? '定期便を直す' : '定期便を登録'} onClose={onClose}>
      <div className="field"><label htmlFor="rt-name">名前（40 文字まで）</label><input id="rt-name" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="例：写真ラボ便 朝" /></div>
      <div className="field"><label htmlFor="rt-tpl">行き先（ひな形）</label>
        <select id="rt-tpl" value={f.templateId} onChange={(e) => setF({ ...f, templateId: e.target.value })}>
          {templates.map((t) => <option key={t.id} value={t.id}>{names.template(t.id)}（{t.stops.map((s) => dir.places.get(s.placeId)?.name ?? '—').join(' → ')}）</option>)}
        </select></div>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        <legend className="muted" style={{ fontSize: 13 }}>くり返し</legend>
        <span style={{ display: 'flex', gap: 12 }}>
          {(['daily', 'weekly', 'monthly'] as const).map((k) => <label key={k}><input type="radio" name="rt-type" style={{ width: 'auto' }} checked={type === k} onChange={() => setType(k)} /> {{ daily: '毎日', weekly: '曜日', monthly: '毎月の日付' }[k]}</label>)}
        </span>
        {type === 'weekly' && <span style={{ display: 'flex', gap: 8 }} role="group" aria-label="曜日">{WEEK.map((w, i) => <label key={w}><input type="checkbox" style={{ width: 'auto' }} checked={week.includes(i)} onChange={(e) => setWeek(e.target.checked ? [...week, i] : week.filter((x) => x !== i))} /> {w}</label>)}</span>}
        {type === 'monthly' && <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input aria-label="日付（「,」で区切る）" value={monthDays} onChange={(e) => setMonthDays(e.target.value)} style={{ width: 140 }} placeholder="例：1,15" /><label><input type="checkbox" style={{ width: 'auto' }} checked={monthEnd} onChange={(e) => setMonthEnd(e.target.checked)} /> 月末</label></span>}
      </fieldset>
      <span style={{ display: 'flex', gap: 8 }}>
        <span className="field" style={{ flex: 1 }}><label htmlFor="rt-start">始まりの日</label><input id="rt-start" type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></span>
        <span className="field" style={{ flex: 1 }}><label htmlFor="rt-end">終わりの日（任意）</label><input id="rt-end" type="date" value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} /></span>
      </span>
      <span style={{ display: 'flex', gap: 8 }}>
        <span className="field" style={{ flex: 1 }}><label htmlFor="rt-time">送る時刻</label><input id="rt-time" type="time" value={f.sendTime} onChange={(e) => setF({ ...f, sendTime: e.target.value })} /></span>
        <span className="field" style={{ flex: 2 }}><label htmlFor="rt-drv">担当（未定でもよい）</label>
          <select id="rt-drv" value={f.driverId} onChange={(e) => setF({ ...f, driverId: e.target.value })}>
            <option value="">未定</option>{drivers.map((d) => <option key={d.id} value={d.id}>{names.driver(d.id)}</option>)}
          </select></span>
      </span>
      <div className="field"><label htmlFor="rt-memo">事務所からのメモ（任意・{RULES.memoMax} 文字まで。毎回の依頼に出ます）</label><input id="rt-memo" maxLength={RULES.memoMax} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="例：受付で名前を言ってください" /></div>
      <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} checked={f.runOnHolidays} onChange={(e) => setF({ ...f, runOnHolidays: e.target.checked })} /> 祝日も走る</label>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>送る時刻になると、担当のアプリに依頼として自動で届きます（勤務していなければ届かず、警告に出ます）。祝日（振替休日を含む）は、「祝日も走る」にしなければ予定を作りません。</p>
      <LockBanner lock={lock} what="この定期便" />
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={busy || !lock.mine} onClick={() => void save()}>保存する</button></div>
    </Dialog>
  );
}
